import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, MapPin, Briefcase, Clock, Building, Star } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

const JobListings = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedType, setSelectedType] = useState('all');

  // Dummy data for demonstration
  useEffect(() => {
    const dummyJobs = [
      {
        id: 1,
        title: 'Frontend Developer',
        company: 'TechCorp Solutions',
        location: 'Mumbai, Maharashtra',
        type: 'Full-time',
        category: 'Frontend Development',
        salary: '₹6-8 LPA',
        experience: '1-3 years',
        description: 'We are looking for a skilled Frontend Developer to join our team...',
        postedDate: '2024-01-15',
        skills: ['React', 'JavaScript', 'HTML', 'CSS'],
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 2,
        title: 'Backend Developer',
        company: 'DataFlow Systems',
        location: 'Bangalore, Karnataka',
        type: 'Full-time',
        category: 'Backend Development',
        salary: '₹8-12 LPA',
        experience: '2-4 years',
        description: 'Join our backend team to build scalable server-side applications...',
        postedDate: '2024-01-14',
        skills: ['Node.js', 'Python', 'MySQL', 'MongoDB'],
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 3,
        title: 'UI/UX Designer',
        company: 'Creative Studios',
        location: 'Delhi, NCR',
        type: 'Contract',
        category: 'Design',
        salary: '₹5-7 LPA',
        experience: '1-2 years',
        description: 'Create beautiful and intuitive user interfaces for our products...',
        postedDate: '2024-01-13',
        skills: ['Figma', 'Adobe XD', 'Sketch', 'Prototyping'],
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 4,
        title: 'DevOps Engineer',
        company: 'CloudTech Solutions',
        location: 'Pune, Maharashtra',
        type: 'Full-time',
        category: 'DevOps',
        salary: '₹10-15 LPA',
        experience: '3-5 years',
        description: 'Manage and optimize our cloud infrastructure and deployment pipelines...',
        postedDate: '2024-01-12',
        skills: ['AWS', 'Docker', 'Kubernetes', 'Jenkins'],
        logo: 'https://via.placeholder.com/50'
      },
      {
        id: 5,
        title: 'Data Scientist',
        company: 'Analytics Pro',
        location: 'Hyderabad, Telangana',
        type: 'Full-time',
        category: 'Data Science',
        salary: '₹12-18 LPA',
        experience: '2-4 years',
        description: 'Build machine learning models and analyze complex datasets...',
        postedDate: '2024-01-11',
        skills: ['Python', 'R', 'TensorFlow', 'SQL'],
        logo: 'https://via.placeholder.com/50'
      }
    ];

    setJobs(dummyJobs);
    setFilteredJobs(dummyJobs);
    setLoading(false);
  }, []);

  const categories = ['all', 'Frontend Development', 'Backend Development', 'Design', 'DevOps', 'Data Science'];
  const locations = ['all', 'Mumbai, Maharashtra', 'Bangalore, Karnataka', 'Delhi, NCR', 'Pune, Maharashtra', 'Hyderabad, Telangana'];
  const jobTypes = ['all', 'Full-time', 'Part-time', 'Contract', 'Internship'];

  const handleSearch = () => {
    let filtered = jobs.filter(job => {
      const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           job.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           job.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = selectedCategory === 'all' || job.category === selectedCategory;
      const matchesLocation = selectedLocation === 'all' || job.location === selectedLocation;
      const matchesType = selectedType === 'all' || job.type === selectedType;

      return matchesSearch && matchesCategory && matchesLocation && matchesType;
    });

    setFilteredJobs(filtered);
  };

  useEffect(() => {
    handleSearch();
  }, [searchTerm, selectedCategory, selectedLocation, selectedType, jobs]);

  const handleSaveJob = (jobId) => {
    // TODO: Implement save job functionality
    console.log('Saving job:', jobId);
  };

  const handleApplyJob = (jobId) => {
    // TODO: Implement apply job functionality
    console.log('Applying for job:', jobId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Job Listings
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Find your dream job from {jobs.length} available positions
          </p>
        </motion.div>

        {/* Search and Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {categories.map(category => (
                <option key={category} value={category}>
                  {category === 'all' ? 'All Categories' : category}
                </option>
              ))}
            </select>

            {/* Location Filter */}
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {locations.map(location => (
                <option key={location} value={location}>
                  {location === 'all' ? 'All Locations' : location}
                </option>
              ))}
            </select>

            {/* Job Type Filter */}
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {jobTypes.map(type => (
                <option key={type} value={type}>
                  {type === 'all' ? 'All Types' : type}
                </option>
              ))}
            </select>
          </div>
        </motion.div>

        {/* Results Count */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <p className="text-gray-600 dark:text-gray-400">
            Showing {filteredJobs.length} of {jobs.length} jobs
          </p>
        </motion.div>

        {/* Job Listings */}
        <div className="space-y-6">
          {filteredJobs.map((job, index) => (
            <motion.div
              key={job.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <img
                    src={job.logo}
                    alt={job.company}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {job.title}
                      </h3>
                      {job.type === 'Full-time' && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                          {job.type}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <div className="flex items-center space-x-1">
                        <Building className="h-4 w-4" />
                        <span>{job.company}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-4 w-4" />
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4" />
                        <span>{job.experience}</span>
                      </div>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                      {job.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {job.skills.slice(0, 3).map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className="px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                      {job.skills.length > 3 && (
                        <span className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded-full">
                          +{job.skills.length - 3} more
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          {job.salary}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          Posted {new Date(job.postedDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => handleSaveJob(job.id)}
                    className="p-2 text-gray-400 hover:text-yellow-500 transition-colors"
                    title="Save Job"
                  >
                    <Star className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="mt-6 flex space-x-3">
                <Link
                  to={`/jobs/${job.id}`}
                  className="flex-1 btn-primary text-center"
                >
                  View Details
                </Link>
                {user?.type === 'student' && (
                  <button
                    onClick={() => handleApplyJob(job.id)}
                    className="flex-1 btn-secondary"
                  >
                    Apply Now
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* No Results */}
        {filteredJobs.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              <Search className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              No jobs found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Try adjusting your search criteria or filters
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default JobListings;
